import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { formatApiErrorBody } from '@/lib/formatApiError';
import { warehouseOptionLabel } from '@/lib/warehouseLabel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Save } from 'lucide-react';
import type { MachineLifecycleStatus } from '@/components/machines';

interface VenueOption {
  id: string;
  name: string;
  code?: string;
}

interface SpokeOption {
  id: string;
  name: string;
  code: string;
}

interface FormData {
  name: string;
  serialNumber: string;
  venueId: string;
  spokeId: string;
  modelType: string;
  slotCount: string;
  ragGreenThreshold: string;
  ragAmberThreshold: string;
  lifecycleStatus: MachineLifecycleStatus;
  plannedSwitchDate: string;
  isActive: boolean;
}

const initialFormData: FormData = {
  name: '',
  serialNumber: '',
  venueId: '',
  spokeId: '',
  modelType: '',
  slotCount: '20',
  ragGreenThreshold: '80',
  ragAmberThreshold: '60',
  lifecycleStatus: 'DRAFT',
  plannedSwitchDate: '',
  isActive: true,
};

export function MachineFormPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isCreate = location.pathname.endsWith('/new');
  const isEdit = !isCreate && Boolean(id);

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [venues, setVenues] = useState<VenueOption[]>([]);
  const [spokes, setSpokes] = useState<SpokeOption[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLookups = useCallback(async () => {
    try {
      const [venuesRes, spokesRes] = await Promise.all([
        apiFetch('/api/v1/venues?pageSize=500&isActive=true'),
        apiFetch('/api/v1/warehouses?type=SPOKE&pageSize=500&isActive=true'),
      ]);

      if (venuesRes.ok) {
        const data = await venuesRes.json();
        setVenues(data.items ?? data ?? []);
      }
      if (spokesRes.ok) {
        const data = await spokesRes.json();
        setSpokes(data.items ?? data ?? []);
      }
    } catch (e) {
      console.error('Failed to load machine form lookups:', e);
    }
  }, []);

  const loadMachine = useCallback(async () => {
    if (!id || isCreate) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/machines/${id}`);
      if (!res.ok) {
        if (res.status === 404) throw new Error('Machine not found');
        throw new Error('Failed to load machine');
      }
      const data = await res.json();
      const machine = data.machine ?? data;
      setFormData({
        name: machine.name ?? '',
        serialNumber: machine.serialNumber ?? '',
        venueId: machine.venueId ?? '',
        spokeId: machine.spokeId ?? '',
        modelType: machine.modelType ?? '',
        slotCount: String(machine.slotCount ?? 20),
        ragGreenThreshold: String(machine.ragGreenThreshold ?? 80),
        ragAmberThreshold: String(machine.ragAmberThreshold ?? 60),
        lifecycleStatus: machine.lifecycleStatus ?? 'LIVE',
        plannedSwitchDate: machine.plannedSwitchDate
          ? machine.plannedSwitchDate.slice(0, 10)
          : '',
        isActive: machine.isActive !== false,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load machine');
    } finally {
      setLoading(false);
    }
  }, [id, isCreate]);

  useEffect(() => {
    loadLookups();
    if (isEdit) loadMachine();
  }, [loadLookups, loadMachine, isEdit]);

  function updateField<K extends keyof FormData>(field: K, value: FormData[K]) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      const green = parseInt(formData.ragGreenThreshold, 10);
      const amber = parseInt(formData.ragAmberThreshold, 10);

      if (!formData.name.trim()) throw new Error('Name is required');
      if (!formData.serialNumber.trim()) throw new Error('Serial number is required');
      if (!formData.venueId) throw new Error('Venue is required');
      if (!formData.spokeId) throw new Error('Spoke warehouse is required');
      if (Number.isNaN(green) || green < 0 || green > 100) {
        throw new Error('Green threshold must be between 0 and 100');
      }
      if (Number.isNaN(amber) || amber < 0 || amber > 100) {
        throw new Error('Amber threshold must be between 0 and 100');
      }
      if (amber > green) {
        throw new Error('Amber threshold must be less than or equal to green threshold');
      }

      const payload: Record<string, unknown> = {
        name: formData.name.trim(),
        serialNumber: formData.serialNumber.trim(),
        venueId: formData.venueId,
        spokeId: formData.spokeId,
        modelType: formData.modelType.trim() || undefined,
        ragGreenThreshold: green,
        ragAmberThreshold: amber,
        lifecycleStatus: formData.lifecycleStatus,
        plannedSwitchDate: formData.plannedSwitchDate.trim()
          ? `${formData.plannedSwitchDate.trim()}T00:00:00.000Z`
          : null,
        isActive: formData.isActive,
      };

      if (isCreate) {
        const slotCount = parseInt(formData.slotCount, 10);
        if (!Number.isFinite(slotCount) || slotCount < 1 || slotCount > 100) {
          throw new Error('Slot count must be between 1 and 100');
        }
        payload.slotCount = slotCount;
      }

      const url = isCreate ? '/api/v1/machines' : `/api/v1/machines/${id}`;
      const method = isCreate ? 'POST' : 'PATCH';

      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let body: unknown;
        try {
          body = await res.json();
        } catch {
          throw new Error('Failed to save machine');
        }
        throw new Error(formatApiErrorBody(body));
      }

      const saved = await res.json();
      const machineId = saved.id ?? id;
      navigate(`/machines/${machineId}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save machine');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading machine…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 mt-0.5"
            onClick={() => navigate(isEdit && id ? `/machines/${id}` : '/machines')}
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground mb-1">
              <Link to="/machines" className="hover:underline">
                Machines
              </Link>
              {' / '}
              {isEdit ? 'Edit' : 'New machine'}
            </p>
            <h1 className="text-xl font-semibold tracking-tight">
              {isEdit ? formData.name || 'Edit machine' : 'Create machine'}
            </h1>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(isEdit && id ? `/machines/${id}` : '/machines')}
          >
            Cancel
          </Button>
          <Button type="submit" form="machine-form" disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 text-sm whitespace-pre-line"
        >
          {error}
        </div>
      )}

      <form id="machine-form" onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Machine details</CardTitle>
            <CardDescription>Name, serial number, and location assignment.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="e.g. Gym Reception Unit 1"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="serialNumber">
                Serial number <span className="text-destructive">*</span>
              </Label>
              <Input
                id="serialNumber"
                value={formData.serialNumber}
                onChange={(e) => updateField('serialNumber', e.target.value)}
                placeholder="e.g. BB-001234"
                className="font-mono text-sm"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="modelType">Model type</Label>
              <Input
                id="modelType"
                value={formData.modelType}
                onChange={(e) => updateField('modelType', e.target.value)}
                placeholder="e.g. BevMax 45"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="venueId">
                Venue <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.venueId || undefined}
                onValueChange={(v) => updateField('venueId', v)}
              >
                <SelectTrigger id="venueId">
                  <SelectValue placeholder="Select venue" />
                </SelectTrigger>
                <SelectContent>
                  {venues.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name}
                      {v.code ? ` (${v.code})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="spokeId">
                Spoke warehouse <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.spokeId || undefined}
                onValueChange={(v) => updateField('spokeId', v)}
              >
                <SelectTrigger id="spokeId">
                  <SelectValue placeholder="Select spoke" />
                </SelectTrigger>
                <SelectContent>
                  {spokes.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {warehouseOptionLabel(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Stock for this machine is replenished from this spoke.
              </p>
            </div>
            {isCreate && (
              <div className="space-y-2">
                <Label htmlFor="slotCount">
                  Slot count <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="slotCount"
                  type="number"
                  min={1}
                  max={100}
                  value={formData.slotCount}
                  onChange={(e) => updateField('slotCount', e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Cannot be changed after creation. Default capacity is 15 units per slot.
                </p>
              </div>
            )}
            {isEdit && (
              <div className="space-y-2">
                <Label>Slot count</Label>
                <Input value={formData.slotCount} disabled className="bg-muted" />
                <p className="text-xs text-muted-foreground">
                  Slot count is fixed after creation. Configure products on the machine detail page.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Lifecycle</CardTitle>
            <CardDescription>
              Cloud statuses stay visible in Express. Unmanaged hides the machine everywhere until you set a Cloud status again. Cloud sync will not overwrite Unmanaged.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="lifecycleStatus">Lifecycle status</Label>
              <Select
                value={formData.lifecycleStatus}
                onValueChange={(value) =>
                  updateField('lifecycleStatus', value as MachineLifecycleStatus)
                }
              >
                <SelectTrigger id="lifecycleStatus">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="SCHEDULED">Scheduled</SelectItem>
                  <SelectItem value="LIVE">Live</SelectItem>
                  <SelectItem value="ARCHIVED">Archived</SelectItem>
                  <SelectItem value="UNMANAGED">Unmanaged</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="plannedSwitchDate">Planned go-live date</Label>
              <Input
                id="plannedSwitchDate"
                type="date"
                value={formData.plannedSwitchDate}
                onChange={(e) => updateField('plannedSwitchDate', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">RAG thresholds</CardTitle>
            <CardDescription>
              Percentage fill levels used for green and amber status on the machines dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="ragGreenThreshold">Green at or above (%)</Label>
              <Input
                id="ragGreenThreshold"
                type="number"
                min={0}
                max={100}
                value={formData.ragGreenThreshold}
                onChange={(e) => updateField('ragGreenThreshold', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ragAmberThreshold">Amber at or above (%)</Label>
              <Input
                id="ragAmberThreshold"
                type="number"
                min={0}
                max={100}
                value={formData.ragAmberThreshold}
                onChange={(e) => updateField('ragAmberThreshold', e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <Checkbox
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) =>
                  updateField('isActive', checked === true)
                }
              />
              <Label htmlFor="isActive">Active</Label>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}

export default MachineFormPage;
